import { ReactNode, createContext, useContext } from 'react';

type MerchantBillingRenderers = {
  /** Every location the merchant can see, each named: Orders, Deliveries. */
  all?: () => ReactNode;
  /** One location's banners: Manage Providers. */
  location?: (locationId: number) => ReactNode;
  /**
   * Takes the merchant to where a card can be added for a location (the
   * vDrive page): its own card while it is still on the vGrubs vDrive card,
   * or with `backup`, a backup card next to the default. Left out where the
   * app has no such screen, and then neither is offered.
   */
  addCard?: (
    location: { id: number; name: string },
    slot?: 'default' | 'backup'
  ) => void;
  /**
   * Opens the merchant's support chat with vGrubs: "Contact Support" where a
   * location has no bank account, since the bank vDrive and SaaS debit (a
   * GoCardless mandate) is connected by support, not in the app. The vPay
   * wallet's Connect Bank is a different account, for payouts.
   */
  contactSupport?: () => void;
};

const MerchantBillingContext = createContext<MerchantBillingRenderers>({});

/**
 * Lets the merchant app put its billing banners on shared pages. The banners,
 * with their pay modals, belong to the partner app; the pages they have to
 * appear on (Orders, Deliveries, Manage Providers) live here in the library
 * and are shared with admin. The partner app provides the renderers once at
 * its root, and admin provides none, so nothing shows there.
 */
export const MerchantBillingProvider = ({
  children,
  ...renderers
}: MerchantBillingRenderers & { children: ReactNode }) => (
  <MerchantBillingContext.Provider value={renderers}>
    {children}
  </MerchantBillingContext.Provider>
);

/** The merchant app's Add Card route, for the banners and popups. */
export const useMerchantAddCard = () =>
  useContext(MerchantBillingContext).addCard;

/** The merchant app's way to vGrubs support, for the pay screens. */
export const useMerchantContactSupport = () =>
  useContext(MerchantBillingContext).contactSupport;

/**
 * Where a shared page shows the merchant's billing banners, under its header
 * like the tablet does. With `locationId`, that location's; without, every
 * location's. Renders nothing outside the merchant app.
 */
export const MerchantBillingNotices = ({
  locationId,
}: {
  locationId?: number;
}) => {
  const renderers = useContext(MerchantBillingContext);
  if (locationId != null) {
    return <>{renderers.location?.(locationId) ?? null}</>;
  }
  return <>{renderers.all?.() ?? null}</>;
};
